<template>
  <div class="add-uploader-user">
    <vjs-modal :name="modalName"
               icon="la la-plus-circle"
               title="Create an Uploader User">
      <template slot="body-content">
        <form id="register-uploader-user" role="form"
              method="post"
              @submit.prevent="saveNewUploaderUser">
          <div class="form-body">
            <div class="form-group">
              <div class="input-group kt-input-group">
                <div class="input-group-prepend">
                  <span class="input-group-text">
                    <i class="fa fa-user" />
                  </span>
                </div>
                <input v-model="name" v-validate="'required|alpha_spaces'"
                       type="text"
                       name="name" :class="{'form-control': true, 'kt-input': true, 'has-error': errors.has('name') }"
                       placeholder="Full Name">
              </div>
              <p v-show="errors.has('name')" class="help is-danger">
                {{ errors.first('name') }}
              </p>
            </div>

            <div class="form-group">
              <div class="input-group kt-input-group">
                <div class="input-group-prepend">
                  <span class="input-group-text">
                    <i class="fa fa-envelope" />
                  </span>
                </div>
                <input v-model="email" v-validate="'required|email'"
                       type="text"
                       name="email" :class="{'form-control': true, 'kt-input': true, 'has-error': errors.has('email') }"
                       placeholder="Enter Email">
              </div>
              <p v-show="errors.has('email')" class="help is-danger">
                {{ errors.first('email') }}
              </p>
            </div>

            <div class="form-group">
              <div class="input-group kt-input-group">
                <div class="input-group-prepend">
                  <span class="input-group-text">
                    <i class="fa fa-building" />
                  </span>
                </div>
                <input type="text" class="form-control kt-input"
                       :placeholder="customer.name"
                       disabled="">
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <div class="input-group kt-input-group">
                    <div class="input-group-prepend">
                      <span class="input-group-text">
                        <i class="fa fa-lock" />
                      </span>
                    </div>
                    <input ref="password" v-model="password"
                           v-validate="'required|min:8|verify_password'"
                           type="password" name="password"
                           class="form-control kt-input" placeholder="Password">
                  </div>
                  <p v-show="errors.has('password')" class="help is-danger">
                    {{ errors.first('password') }}
                  </p>
                </div>
              </div>
              <div class="col-md-6">
                <div class="input-group kt-input-group">
                  <div class="input-group-prepend">
                    <span class="input-group-text">
                      <i class="fa fa-lock" />
                    </span>
                  </div>
                  <input v-model="password_confirmation" v-validate="'required|confirmed:password'"
                         data-vv-as="password confirmation"
                         type="password" name="password_confirmation"
                         class="form-control kt-input" placeholder="Confirm Password">
                </div>
                <p v-show="errors.has('password_confirmation')" class="help is-danger">
                  {{ errors.first('password_confirmation') }}
                </p>
              </div>
              <div class="col-12 col-md-6">
                <div class="form-group">
                  <label for="expire_on">Expire On</label>
                  <div class="input-group kt-input-group">
                    <datetime id="expire_on" v-model="expire_on"
                              type="date"
                              input-class="form-control kt-input"
                              format="yyyy-MM-dd" />
                  </div>
                </div>
              </div>
              <div v-if="uploader_profiles.length" class="col-12 col-md-6">
                <div class="form-group">
                  <label for="uploader_profile">Uploader Profile <small>Used to create items on approval.</small></label>
                  <select id="uploader_profile" v-model="selected_uploader_profile" class="form-control">
                    <option :value="null" />
                    <option v-for="uploader_profile in uploader_profiles" :key="uploader_profile.id" :value="uploader_profile.id">
                      {{ uploader_profile.name }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </form>
      </template>
      <template slot="body-controls">
        <button type="button" class="btn btn-ss-standout" @click="saveNewUploaderUser">
          <i class="la la-plus-circle" /> Create User
        </button>
        <button type="button" class="btn btn-ss-cancel" @click="$modal.hide(modalName)">
          Cancel
        </button>
      </template>
    </vjs-modal>
  </div>
</template>

<script>
import VeeValidate from 'vee-validate';
import VjsModal from "VueModals/VjsModal";

Vue.use(VeeValidate);
// password rules: 1 upper, 1 lower, 1 number, 1 special character, minimum 8
VeeValidate.Validator.extend('verify_password', {
  getMessage: () =>
    `1 uppercase letter, 1 lowercase letter, 1 number, and one special character`,
  validate: (value) => {
    const strong_regex = new RegExp(
      '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})',
    );
    return strong_regex.test(value);
  },
});

export default {
  name: 'AddUser',
  components: {
    VjsModal,
  },
  props: {
    customer: {
      type: Object,
      required: true,
    },
    modalName: {
      type: String,
      required: false,
      default: `add-uploader-modal`,
    },
  },
  data: () => ({
    formIncomplete: false,
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    expire_on: null,
    uploader_profiles: [],
    selected_uploader_profile: null,
  }),
  mounted() {
    this.getUploaderProfiles();
  },
  methods: {
    getUploaderProfiles() {
      if (!this.permissionsCanAccessFeature('uploader_profiles')) {
        return;
      }
      axios.get(`/api/v1/uploader-profiles`).then((response) => {
        this.uploader_profiles = response.data.data;
      }).catch((err) => {
        window.handleAxiosError(err);
        toastr.error("Could not retrieve uploader profiles");
      });
    },
    saveNewUploaderUser() {
      const form_data = {
        name: this.name,
        email: this.email,
        password: this.password,
        password_confirmation: this.password_confirmation,
        user_type: 'uploader',
        expire_on: this.expire_on,
        uploader_profile_id: this.selected_uploader_profile ? this.selected_uploader_profile : null,
      };

      this.$validator.validateAll().then((result) => {
        if (result) {
          axios
            .post(`/api/v1/user`, form_data)
            .then((response) => {
              this.$modal.hide(this.modalName);
              if (response.data && response.data.data) {
                toastr.success('User Added');
                EventBus.$emit('user-added', response.data.data);
                this.resetForm();
              }
            })
            .catch((e) => {
              window.handleAxiosError(e);
            });
        } else {
          toastr.error('Some fields have not been completed correctly');
        }
      });
    },
    resetForm() {
      this.name = '';
      this.email = '';
      this.password = '';
      this.password_confirmation = '';
      this.errors.clear();
    },
  },
};
</script>

<style scoped lang='scss'>
    .add-user {
        .add-user-button {
            margin-bottom: 20px;
        }
    }

    input.has-error {
        border: 1px solid #e73d4a;
    }

    p.is-danger {
        margin-top: 5px;
        color: #e73d4a;
    }

    .mt-checkbox-list {
        .mt-checkbox:last-child {
            margin-bottom: 0;
        }
    }

    .form {
        .custom-alerts {
            margin: 0 20px;
            p {
                margin: 5px 0;
            }
            i {
                padding-right: 10px;
            }
        }
    }
</style>
